/**
 * usePaymentAgentReadiness — hook do backend Global Readiness Gate (Sprint 1, E4).
 *
 * Backend `GET /api/payment-agent/readiness` jest JEDYNYM źródłem prawdy —
 * hook wyłącznie pobiera i udostępnia kontrakt bez rekonstruowania logiki.
 *
 * Odświeżanie:
 *   - fetch przy mount + jawne `refetch()` przez consumer,
 *   - opcjonalny lekki polling co `POLL_INTERVAL_MS` — spokojny, nie
 *     przeszkadza użytkownikowi.
 *   - integracja z existing event `pax:packages:refresh` (payment_packages
 *     wysyła go po istotnych akcjach — refetch readiness razem z nimi).
 *
 * NIE tworzymy agresywnego pollingu (kontrakt zmienia się rzadko —
 * generation bump czy job status change).
 */
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Spokojny polling: co 30s — wystarczy do refleksji zmian generation
// pomiędzy user's actions (import KSeF, wgranie wyciągu, itp.).
const POLL_INTERVAL_MS = 30_000;

export function usePaymentAgentReadiness({ pollingEnabled = true } = {}) {
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);
  const intervalRef = useRef(null);

  const fetchReadiness = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/payment-agent/readiness`, {
        withCredentials: true,
      });
      if (!mountedRef.current) return;
      setReadiness(resp.data);
      setError(null);
    } catch (e) {
      if (!mountedRef.current) return;
      // Fail-closed: brak danych = nie pokazujemy stanu ready.
      setError(e?.response?.data?.detail || e?.message || "Błąd pobierania statusu gotowości");
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchReadiness();
    if (pollingEnabled) {
      intervalRef.current = setInterval(fetchReadiness, POLL_INTERVAL_MS);
    }

    // Integracja z istniejącym event bus payment-packages:
    // po każdej akcji (analiza, import KSeF, cancel, itp.) — refresh readiness.
    const onRefresh = () => fetchReadiness();
    window.addEventListener("pax:packages:refresh", onRefresh);

    return () => {
      mountedRef.current = false;
      window.removeEventListener("pax:packages:refresh", onRefresh);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchReadiness, pollingEnabled]);

  return {
    readiness, // pełny kontrakt backendu (ready, blocking_reasons, ksef, statement_analysis, …)
    loading,
    error,
    refetch: fetchReadiness,
  };
}
